// /app/frontend/src/pages/compliance/readiness/ReadinessCreatePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../../components/ui/alert-dialog';

import { readinessAPI, clientsAPI, entitiesAPI } from '../../../services/api';
import { JURISDICTIONS } from '../../../constants/compliance';

// Question counts match the backend question bank (keep in sync with
// backend/data/readiness_questions.py).
const ASSESSMENT_TYPES = [
  { value: 'enterprise', label: 'Enterprise', description: '71 questions — comprehensive assessment' },
  { value: 'sme', label: 'SME', description: '37 questions — streamlined assessment' }
];

// Default expiry: 30 days from now (matches backend DEFAULT_EXPIRY_DAYS).
const DEFAULT_EXPIRY_DAYS = 30;
const defaultExpiryISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + DEFAULT_EXPIRY_DAYS);
  return d.toISOString().slice(0, 10);  // YYYY-MM-DD for <input type="date">
};
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function ReadinessCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  
  const [clients, setClients] = useState([]);
  const [entities, setEntities] = useState([]);
  
  const [formData, setFormData] = useState({
    client_id: '',
    entity_id: '',
    assessment_type: 'enterprise',
    jurisdiction: '',
    recipient_name: '',
    recipient_email: '',
    recipient_role: '',
    notes: '',
    expires_at: defaultExpiryISO(),  // Bug #8: DPO can override the default +30 day deadline
  });

  useEffect(() => {
    loadClients();
    if (isEdit) {
      loadAssessment();
    }
  }, [id]);

  useEffect(() => {
    if (formData.client_id) {
      loadEntities(formData.client_id);
    } else {
      setEntities([]);
    }
  }, [formData.client_id]);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadEntities = async (clientId) => {
    try {
      const response = await entitiesAPI.getAll(clientId);
      setEntities(response.data);
    } catch (error) {
      console.error('Error loading entities:', error);
    }
  };

  const loadAssessment = async () => {
    try {
      setLoading(true);
      const response = await readinessAPI.getById(id);
      const data = response.data;
      setFormData({
        client_id: data.client_id,
        entity_id: data.entity_id,
        assessment_type: data.assessment_type,
        jurisdiction: data.jurisdiction,
        recipient_name: data.recipient_name,
        recipient_email: data.recipient_email,
        recipient_role: data.recipient_role || '',
        notes: data.notes || '',
        // Backend stores ISO datetime; input expects YYYY-MM-DD.
        expires_at: (data.expires_at || '').slice(0, 10) || defaultExpiryISO(),
      });
    } catch (error) {
      console.error('Error loading assessment:', error);
      toast.error('Failed to load assessment');
      navigate('/compliance/readiness');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (andSend = false) => {
    // Validation
    if (!formData.client_id || !formData.entity_id || !formData.jurisdiction || 
        !formData.recipient_name || !formData.recipient_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate expiry date is in the future
    if (formData.expires_at && formData.expires_at < todayISO()) {
      toast.error('Expiry date cannot be in the past');
      return;
    }

    try {
      setSaving(true);
      let assessmentId = id;

      // Convert YYYY-MM-DD to end-of-day ISO datetime so the backend
      // parses it as a timezone-aware ISO string.
      const payload = {
        ...formData,
        expires_at: formData.expires_at
          ? new Date(`${formData.expires_at}T23:59:59Z`).toISOString()
          : undefined,
      };

      if (isEdit) {
        await readinessAPI.update(id, payload);
        toast.success('Assessment updated');
      } else {
        const response = await readinessAPI.create(payload);
        assessmentId = response.data.id;
        toast.success('Assessment created');
      }
      
      if (andSend && assessmentId) {
        setSending(true);
        await readinessAPI.send(assessmentId);
        toast.success('Assessment sent to recipient');
      }
      
      navigate(`/compliance/readiness/${assessmentId}`);
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
      setSending(false);
    }
  };

  const selectedJurisdiction = JURISDICTIONS.find(j => j.value === formData.jurisdiction);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="readiness-create-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/readiness')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEdit ? 'Edit Assessment' : 'New Readiness Assessment'}
            </h1>
            <p className="text-muted-foreground">
              Create a baseline diagnostic assessment for a client
            </p>
          </div>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Client & Entity</CardTitle>
            <CardDescription>Select the client and entity for this assessment</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select 
                  value={formData.client_id} 
                  onValueChange={(v) => {
                    handleChange('client_id', v);
                    handleChange('entity_id', '');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.client_name || client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entity *</Label>
                <Select 
                  value={formData.entity_id} 
                  onValueChange={(v) => handleChange('entity_id', v)}
                  disabled={!formData.client_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select entity" />
                  </SelectTrigger>
                  <SelectContent>
                    {entities.map(entity => (
                      <SelectItem key={entity.id} value={entity.id}>
                        {entity.entity_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assessment Configuration</CardTitle>
            <CardDescription>Choose the assessment type and applicable jurisdiction</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assessment Type *</Label>
                <Select 
                  value={formData.assessment_type} 
                  onValueChange={(v) => handleChange('assessment_type', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSESSMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-xs text-muted-foreground">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Jurisdiction *</Label>
                <Select 
                  value={formData.jurisdiction} 
                  onValueChange={(v) => handleChange('jurisdiction', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select jurisdiction" />
                  </SelectTrigger>
                  <SelectContent>
                    {JURISDICTIONS.map(jur => (
                      <SelectItem key={jur.value} value={jur.value}>
                        {jur.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedJurisdiction && (
              <div className="bg-muted rounded-lg p-4 text-sm">
                <span className="text-muted-foreground">Applicable Law: </span>
                <span className="font-medium">
                  {selectedJurisdiction.act || 'Caribbean Data Protection Framework (no enacted law yet)'}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Deadline</CardTitle>
            <CardDescription>
              Defaults to 30 days from today. You can shorten or extend it below.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Label htmlFor="expires-at-input">Expiry Date *</Label>
            <Input
              id="expires-at-input"
              type="date"
              min={todayISO()}
              value={formData.expires_at}
              onChange={(e) => handleChange('expires_at', e.target.value)}
              className="max-w-xs"
            />
            <p className="text-xs text-muted-foreground">
              After this date the respondent link stops working and the assessment is
              marked as expired.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recipient Details</CardTitle>
            <CardDescription>Who will complete this assessment?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Recipient Name *</Label>
                <Input
                  value={formData.recipient_name}
                  onChange={(e) => handleChange('recipient_name', e.target.value)}
                  placeholder="e.g., John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Recipient Email *</Label>
                <Input
                  type="email"
                  value={formData.recipient_email}
                  onChange={(e) => handleChange('recipient_email', e.target.value)}
                  placeholder="e.g., john@company.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role/Title (Optional)</Label>
              <Input
                value={formData.recipient_role}
                onChange={(e) => handleChange('recipient_role', e.target.value)}
                placeholder="e.g., Compliance Manager"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notes (Optional)</CardTitle>
            <CardDescription>Internal notes about this assessment</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any additional context or notes..."
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button variant="outline" onClick={() => navigate('/compliance/readiness')}>
            Cancel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handleSave(false)}
            disabled={saving || sending}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save as Draft
          </Button>
          <Button 
            onClick={() => setShowSendDialog(true)}
            disabled={saving || sending}
          >
            <Send className="h-4 w-4 mr-2" />
            Save & Send
          </Button>
        </div>

        {/* Send Confirmation Dialog */}
        <AlertDialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send Assessment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will save the assessment and send an email invitation to{' '}
                <strong>{formData.recipient_name}</strong> at{' '}
                <strong>{formData.recipient_email}</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => {
                setShowSendDialog(false);
                handleSave(true);
              }}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
